/**
 * Avisos de fim dos jobs em lote, em qualquer tela (spec BULK-06, HAND-05).
 *
 * O resolver em lote e o repasse de tickets na exclusão rodam no worker e podem
 * levar minutos; quem iniciou pode ter fechado o dialog e mudado de página. O
 * socket manda o andamento só para essa pessoa, e este componente vira toast.
 * O id do toast é o jobId: progresso e fim substituem o mesmo aviso.
 */
import { useEffect } from 'react';
import { toast } from 'sonner';
import { useSocket } from '@/contexts/SocketContext';

const n = (v) => Number(v || 0).toLocaleString('pt-BR');
const one = (v) => Number(v) === 1;
const tickets = (v) => `${n(v)} ${one(v) ? 'ticket' : 'tickets'}`;

export function bulkResolveDoneToast(job) {
    if (!job?.jobId) return;
    if (job.status === 'done') {
        toast.success(`${tickets(job.resolved)} ${one(job.resolved) ? 'resolvido' : 'resolvidos'} em lote`, {
            id: job.jobId,
            description:
                job.skipped > 0
                    ? `${tickets(job.skipped)} ${one(job.skipped) ? 'ignorado porque mudou' : 'ignorados porque mudaram'} durante o processo.`
                    : undefined,
        });
    } else if (job.status === 'cancelled') {
        toast.warning('Resolução em lote interrompida', {
            id: job.jobId,
            description: `${tickets(job.resolved)} resolvidos, ${n(job.remaining)} continuam abertos.`,
        });
    } else if (job.status === 'failed') {
        toast.error('A resolução em lote parou por um erro', {
            id: job.jobId,
            description: `${tickets(job.resolved)} foram resolvidos antes da falha.`,
        });
    }
}

const HANDOVER_VERB = { queue: 'Devolvendo', distribute: 'Distribuindo', user: 'Transferindo' };

export function handoverProgressToast(state) {
    if (!state?.jobId) return;
    toast.loading(`${state.userName || 'Usuário'}: acesso removido`, {
        id: state.jobId,
        description: `${HANDOVER_VERB[state.mode] || 'Transferindo'} ${tickets(state.total)}... ${n(state.processed)} de ${n(state.total)}`,
    });
}

export function handoverDoneToast(state) {
    if (!state?.jobId) return;
    const who = state.userName || 'o usuário';
    if (state.status === 'failed') {
        toast.error('O repasse dos tickets parou por um erro', {
            id: state.jobId,
            description: `${tickets(state.moved)} de ${who} foram repassados antes da falha.`,
        });
        return;
    }
    const where = {
        queue: `Os tickets de ${who} voltaram para a fila.`,
        distribute: `Os tickets de ${who} foram distribuídos entre os agentes.`,
        user: `Os tickets de ${who} estão com ${state.targetName || 'o usuário escolhido'}.`,
    };
    toast.success(`${tickets(state.moved)} ${one(state.moved) ? 'repassado' : 'repassados'}`, {
        id: state.jobId,
        description: where[state.mode] || where.queue,
    });
}

export function BulkJobToasts() {
    const { socket } = useSocket();

    useEffect(() => {
        if (!socket) return undefined;
        socket.on('bulkResolve:done', bulkResolveDoneToast);
        socket.on('userHandover:progress', handoverProgressToast);
        socket.on('userHandover:done', handoverDoneToast);
        return () => {
            socket.off('bulkResolve:done', bulkResolveDoneToast);
            socket.off('userHandover:progress', handoverProgressToast);
            socket.off('userHandover:done', handoverDoneToast);
        };
    }, [socket]);

    return null;
}
